import React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Tooltip,
  Checkbox,
  Alert,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ColorLegend from "./ColorLegend";
import DataTable from "./DataTable";
import { styles } from "./DataTable.styles";
import ScreenRotationIcon from "@mui/icons-material/ScreenRotation";
import { FONT_SIZES } from "../../constants/fontSizes";
import { useMediaQuery } from "@mui/material";

const getBrandNameScoreColor = (score) => {
  if (score < 2) return styles.highRiskColor; // Red - Poor
  if (score < 4) return styles.mediumRiskColor; // Orange - Good
  return styles.lowRiskColor; // Green - Excellent
};

const tooltipStyles = {
  tooltip: {
    sx: {
      fontSize: FONT_SIZES.TEXT_HINT.xs,
      "@media (min-width: 600px)": {
        fontSize: FONT_SIZES.TEXT_HINT.sm,
      },
    },
  },
};

const columnTooltips = {
  brand_appeal: "How appealing and attractive is the name as a brand?",
  memorability: "How easy is it to remember the name?",
  relevance: "How well does the name align with your business or product?",
  uniqueness: "How distinctive and original is the name?",
  seo_searchability: "How well will the name perform in search engines?",
  emotional_resonance: "How emotionally engaging and resonant is the name?",
  vibe: "The overall feel or aesthetic of the name",
};

const BrandNameScoresReport = ({
  brandNameScoresData,
  getDetailedRowColor,
  expandAccordian,
  onAccordionChange,
  isMobile,
  showBrandNameScoresReport,
  selectedRows,
  handleRowSelect,
  flipColumns,
  handleSelectAll,
}) => {
  if (!showBrandNameScoresReport) {
    return (
      <>
        <Typography variant="h6" sx={styles.reportTitle}>
          Name Ideation Report
        </Typography>
        <Alert severity="info" icon={<ScreenRotationIcon />} sx={styles.alert}>
          Rotate your device to landscape mode to view report table.
        </Alert>
      </>
    );
  }
  const numericWidth = "8%";

  const brandNameScoreColumns = [
    {
      id: "select",
      label: "Add to Input",
      width: "5%",
      format: (_, row) => (
        <div style={styles.checkboxContainer}>
          <Checkbox
            size="small"
            checked={selectedRows.has(row.name)}
            onChange={() => handleRowSelect(row.name)}
          />
        </div>
      ),
      headerFormat: () => {
        return (
          <div style={styles.checkboxHeaderContainer}>
            {!isMobile && <span>Add to Input</span>}
            <Checkbox
              size="small"
              checked={
                brandNameScoresData.length > 0 &&
                brandNameScoresData.every((row) => selectedRows.has(row.name))
              }
              indeterminate={
                brandNameScoresData.some((row) => selectedRows.has(row.name)) &&
                !brandNameScoresData.every((row) => selectedRows.has(row.name))
              }
              onChange={handleSelectAll}
            />
          </div>
        );
      },
    },
    {
      id: "name",
      label: "Candidate Name",
      width: "15%",
    },
    {
      id: "brand_appeal",
      label: "Brand Appeal",
      width: numericWidth,
      tooltip: columnTooltips.brand_appeal,
      tooltipProps: tooltipStyles,
      format: (value) => value || 0,
      getColor: (value) => getBrandNameScoreColor(value || 0),
    },
    {
      id: "memorability",
      label: "Memorability",
      width: numericWidth,
      tooltip: columnTooltips.memorability,
      tooltipProps: tooltipStyles,
      format: (value) => value || 0,
      getColor: (value) => getBrandNameScoreColor(value || 0),
    },
    {
      id: "relevance",
      label: "Relevance",
      width: numericWidth,
      tooltip: columnTooltips.relevance,
      tooltipProps: tooltipStyles,
      format: (value) => value || 0,
      getColor: (value) => getBrandNameScoreColor(value || 0),
    },
    {
      id: "uniqueness",
      label: "Uniqueness",
      width: numericWidth,
      tooltip: columnTooltips.uniqueness,
      tooltipProps: tooltipStyles,
      format: (value) => value || 0,
      getColor: (value) => getBrandNameScoreColor(value || 0),
    },
    {
      id: "seo_searchability",
      label: "SEO & Searchability",
      width: numericWidth,
      tooltip: columnTooltips.seo_searchability,
      tooltipProps: tooltipStyles,
      format: (value) => value || 0,
      getColor: (value) => getBrandNameScoreColor(value || 0),
    },
    {
      id: "emotional_resonance",
      label: "Emotional Resonance",
      width: numericWidth,
      tooltip: columnTooltips.emotional_resonance,
      tooltipProps: tooltipStyles,
      format: (value) => value || 0,
      getColor: (value) => getBrandNameScoreColor(value || 0),
    },
    {
      id: "vibe",
      label: "Vibe",
      width: "20%",
      tooltip: columnTooltips.vibe,
      tooltipProps: tooltipStyles,
      format: (value) => {
        if (!value) return "";
        return (
          <Tooltip
            title={value}
            placement="top"
            arrow
            enterDelay={200}
            leaveDelay={200}
          >
            <span style={styles.tooltipText}>
              <span className="vibe-truncate">
                {value.length > 100 ? `${value.substring(0, 100)}...` : value}
              </span>
              <span className="vibe-full">{value}</span>
            </span>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <Accordion
      expanded={expandAccordian}
      onChange={onAccordionChange}
      sx={styles.accordion}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h6" sx={styles.accordionTitle}>
          Name Ideation Report
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        {isMobile ? (
          <Alert
            severity="info"
            icon={<ScreenRotationIcon />}
            sx={styles.alert}
          >
            Rotate your device to landscape mode to view report table.
          </Alert>
        ) : (
          <>
            <ColorLegend
              legendText={["Excellent (4-5)", "Good (2-3)", "Poor (0-1)"]}
              legendColor={[
                styles.lowRiskColor,
                styles.mediumRiskColor,
                styles.highRiskColor,
              ]}
            />
            <DataTable
              columns={brandNameScoreColumns}
              data={brandNameScoresData}
              getRowColor={getDetailedRowColor}
              isReportData={false}
              sx={styles.dataTable}
              isMobile={isMobile}
              flipColumns={flipColumns}
              isDomainUtility={true}
            />
          </>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default BrandNameScoresReport;
